import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography } from 'antd';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

/**
 * PageModal — the one modal shell for the app, the dialog twin of PageHeader.
 *
 *   ┌───────────────────────────────────────────┐
 *   │ [icon]  Title  [meta]                  ✕  │  ← header: its own band, never scrolls
 *   │         one-line description              │
 *   ├───────────────────────────────────────────┤
 *   │  children                                 │  ← body: the only part that scrolls (thin scrollbar)
 *   ├───────────────────────────────────────────┤
 *   │                       [Cancel] [Primary]  │  ← footer (optional): its own band, never scrolls
 *   └───────────────────────────────────────────┘
 *
 *   <PageModal
 *     open={open}
 *     onCancel={close}
 *     icon={<Icons.Plus size={22} />}
 *     title="Issue Batch"
 *     description="Create a batch from an approved MBMR."
 *     meta={<Tag>Trial</Tag>}                 // chips beside the title
 *     width={1140}                            // default 720
 *     footer={<Button type="primary">Save</Button>}   // omit for a body-only dialog (the form brings its own buttons)
 *   >…form…</PageModal>
 *
 * It sits above the app's fixed footer (z-index 1500), is at most the window height minus a margin, and keeps the header and
 * footer in view while the body scrolls. Every other Modal prop (destroyOnHidden, maskClosable, afterClose …) passes through.
 */

const { Text } = Typography;

// the shell's fixed footer is z-index 1100; dialogs sit above it (antd stacks their popups above this value)
const MODAL_Z_INDEX = 1500;

export default function PageModal({
  open,
  onCancel,
  icon,
  title,
  description,
  meta,
  width = 720,
  footer = null,
  bodyStyle,
  children,
  ...rest
}) {
  const { isDark } = useTheme();
  const border = isDark ? defaultColors.palette.gray[700] : '#e5e7eb';
  const surface = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const band = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const titleColor = isDark ? '#f9fafb' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';

  const header = (
    <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
      {icon && (
        <span className="flex items-center justify-center shrink-0" style={{ color: '#2563eb', width: description ? 40 : 24 }}>
          {React.isValidElement(icon) ? React.cloneElement(icon, { size: description ? 32 : 22 }) : icon}
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: 18, fontWeight: 700, lineHeight: '24px', color: titleColor }}>{title}</span>
          {meta}
        </div>
        {description && <Text style={{ display: 'block', fontSize: 12, lineHeight: '16px', color: muted, whiteSpace: 'normal' }}>{description}</Text>}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={header}
      footer={footer}
      width={width}
      centered
      zIndex={MODAL_Z_INDEX}
      classNames={{ body: 'page-card-scroll' }}
      styles={{
        content: { padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100dvh - 48px)', overflow: 'hidden', backgroundColor: surface },
        header: { margin: 0, padding: '14px 56px 14px 24px', borderBottom: `1px solid ${border}`, backgroundColor: band, flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px', backgroundColor: surface, ...bodyStyle },
        footer: { margin: 0, padding: '12px 24px', borderTop: `1px solid ${border}`, backgroundColor: band, flexShrink: 0 },
      }}
      {...rest}
    >
      {children}
    </Modal>
  );
}

PageModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  icon: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  meta: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  footer: PropTypes.node,
  bodyStyle: PropTypes.object,
  children: PropTypes.node,
};
